import React, { useEffect, useId, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { fetchJsonCached, peekCachedJson } from "./apiCache";
import LoadingState from "./LoadingState";
import { AUDITORIES_BUILDING_1, AUDITORIES_BUILDING_2 } from "./auditories_const";

const EMPTY_AUDITORIES = [];
const SELECTED_DATE_CACHE_KEY = "auditories:selectedDate";
const SELECTED_BUILDING_CACHE_KEY = "auditories:selectedBuilding";
const SELECTED_AUDITORY_CACHE_KEY = "auditories:selectedAuditory";
const SOURCE_VALUE_ALIASES = {
  SPO: "СПО",
  VO: "ВО",
  OZO: "ОЗО",
  "ÑÏÎ": "СПО",
  "ÂÎ": "ВО",
  "ÎÇÎ": "ОЗО",
};
const SOURCE_BUILDING_FALLBACKS = {
  СПО: "1",
  ВО: "2",
  ОЗО: "2",
};

const MOBILE_PICKER_BODY_WIDTH = 55;
const MOBILE_PICKER_TOTAL_WIDTH = 69;
const MOBILE_PICKER_BODY_OFFSET_X = MOBILE_PICKER_TOTAL_WIDTH - MOBILE_PICKER_BODY_WIDTH;
const MOBILE_PICKER_BUTTON_SIZE = 47;
const MOBILE_PICKER_GAP_ITEM_HEIGHT = 9; // my-1 (4px) + h-px (1px) + my-1 (4px)
const MOBILE_PICKER_GAP = 8;
const MOBILE_PICKER_PADDING = 4;
const MOBILE_PICKER_RADIUS = 24;
const MOBILE_PICKER_BULGE_RADIUS = MOBILE_PICKER_BUTTON_SIZE / 2;

function buildRoundedRectPath(left, top, width, height, radius) {
  const right = left + width;
  const bottom = top + height;
  return [
    `M ${left + radius} ${top}`,
    `H ${right - radius}`,
    `A ${radius} ${radius} 0 0 1 ${right} ${top + radius}`,
    `V ${bottom - radius}`,
    `A ${radius} ${radius} 0 0 1 ${right - radius} ${bottom}`,
    `H ${left + radius}`,
    `A ${radius} ${radius} 0 0 1 ${left} ${bottom - radius}`,
    `V ${top + radius}`,
    `A ${radius} ${radius} 0 0 1 ${left + radius} ${top}`,
    "Z",
  ].join(" ");
}

function buildMobilePickerPath(totalHeight, bulgeCenterY = null) {
  const left = MOBILE_PICKER_BODY_OFFSET_X;
  const top = 0.5;
  const width = MOBILE_PICKER_BODY_WIDTH - 0.5;
  const height = totalHeight - 1;
  if (bulgeCenterY == null) return buildRoundedRectPath(left, top, width, height, MOBILE_PICKER_RADIUS);

  const right = left + width;
  const bottom = top + height;
  const topCornerY = top + MOBILE_PICKER_RADIUS;
  const bottomCornerY = bottom - MOBILE_PICKER_RADIUS;
  const safeCy = Math.max(
    topCornerY + MOBILE_PICKER_BULGE_RADIUS,
    Math.min(bottomCornerY - MOBILE_PICKER_BULGE_RADIUS, bulgeCenterY)
  );
  const bulgeTop = safeCy - MOBILE_PICKER_BULGE_RADIUS;
  const bulgeBottom = safeCy + MOBILE_PICKER_BULGE_RADIUS;

  return [
    `M ${left + MOBILE_PICKER_RADIUS} ${top}`,
    `H ${right - MOBILE_PICKER_RADIUS}`,
    `A ${MOBILE_PICKER_RADIUS} ${MOBILE_PICKER_RADIUS} 0 0 1 ${right} ${topCornerY}`,
    `V ${bottomCornerY}`,
    `A ${MOBILE_PICKER_RADIUS} ${MOBILE_PICKER_RADIUS} 0 0 1 ${right - MOBILE_PICKER_RADIUS} ${bottom}`,
    `H ${left + MOBILE_PICKER_RADIUS}`,
    `A ${MOBILE_PICKER_RADIUS} ${MOBILE_PICKER_RADIUS} 0 0 1 ${left} ${bottomCornerY}`,
    `V ${bulgeBottom}`,
    `A ${MOBILE_PICKER_BULGE_RADIUS} ${MOBILE_PICKER_BULGE_RADIUS} 0 0 0 ${left} ${bulgeTop}`,
    `V ${topCornerY}`,
    `A ${MOBILE_PICKER_RADIUS} ${MOBILE_PICKER_RADIUS} 0 0 1 ${left + MOBILE_PICKER_RADIUS} ${top}`,
    "Z",
  ].join(" ");
}

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function toLocalIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function toHhMm(date) {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function parseIsoDate(isoDate) {
  if (!isoDate || typeof isoDate !== "string") return null;
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return new Date(year, month - 1, day);
}

function formatDateLabel(isoDate) {
  const date = parseIsoDate(isoDate);
  if (!date) return isoDate;
  return new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit" }).format(date);
}

function formatWeekdayLabel(isoDate) {
  const date = parseIsoDate(isoDate);
  if (!date) return "";
  return new Intl.DateTimeFormat("ru-RU", { weekday: "short" }).format(date).replace(".", "");
}

function isSunday(isoDate) {
  const date = parseIsoDate(isoDate);
  return date ? date.getDay() === 0 : false;
}

function findNearestDate(targetIso, dates) {
  if (!dates?.length) return "";
  if (!targetIso) return dates[0];
  if (dates.includes(targetIso)) return targetIso;
  const targetDate = parseIsoDate(targetIso);
  if (!targetDate) return dates[0];
  let best = dates[0];
  let bestDelta = Math.abs(parseIsoDate(best) - targetDate);
  for (let i = 1; i < dates.length; i += 1) {
    const current = dates[i];
    const delta = Math.abs(parseIsoDate(current) - targetDate);
    if (delta < bestDelta) {
      best = current;
      bestDelta = delta;
    }
  }
  return best;
}

function scrollContainerToChild(container, child, { axis = "y", behavior = "smooth", align = "center" } = {}) {
  if (!container || !child) return;
  const containerRect = container.getBoundingClientRect();
  const childRect = child.getBoundingClientRect();

  if (axis === "x") {
    const childOffset = childRect.left - containerRect.left + container.scrollLeft;
    const targetLeft =
      align === "center"
        ? childOffset - container.clientWidth / 2 + childRect.width / 2
        : childOffset;
    container.scrollTo({ left: Math.max(0, targetLeft), behavior });
    return;
  }

  const childOffset = childRect.top - containerRect.top + container.scrollTop;
  const targetTop =
    align === "center"
      ? childOffset - container.clientHeight / 2 + childRect.height / 2
      : childOffset;
  container.scrollTo({ top: Math.max(0, targetTop), behavior });
}

function expandIsoDateRange(startIso, endIso) {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (!start || !end || start > end) return [];
  const out = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    out.push(toLocalIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

function getReferenceNow() {
  if (typeof window === "undefined") {
    return { date: new Date(), simulated: false };
  }

  const params = new URLSearchParams(window.location.search);
  const dayOffsetRaw = params.get("dayOffset");
  const atRaw = params.get("at");
  const date = new Date();
  let simulated = false;

  if (dayOffsetRaw !== null && dayOffsetRaw !== "") {
    const dayOffset = Number(dayOffsetRaw);
    if (Number.isFinite(dayOffset)) {
      date.setDate(date.getDate() + dayOffset);
      simulated = true;
    }
  }

  if (typeof atRaw === "string" && atRaw.trim()) {
    const [h, m] = atRaw.trim().split(":").map(Number);
    if (Number.isFinite(h) && Number.isFinite(m) && h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      date.setHours(h, m, 0, 0);
      simulated = true;
    }
  }

  return { date, simulated };
}

function extractRoom(item) {
  if (item?.auditory_norm) return String(item.auditory_norm).trim();
  const raw = String(item?.auditory || "").trim();
  if (!raw) return "";
  return raw
    .replace(/^ауд\.?\s*/i, "")
    .replace(/^каб\.?\s*/i, "")
    .trim();
}

function detectBuildingFromRaw(raw) {
  if (!raw) return null;
  const text = String(raw).trim();
  if (!text) return null;
  if (/\bук\s*1\b|\bук1\b|\bкорпус\s*1\b/i.test(text)) return "1";
  if (/\bук\s*2\b|\bук2\b|\bкорпус\s*2\b/i.test(text)) return "2";
  return null;
}

function normalizeSource(source) {
  const text = String(source || "").trim().toUpperCase();
  if (!text) return "";
  return SOURCE_VALUE_ALIASES[text] || text;
}

function normalizeRoomLabel(label) {
  const text = String(label || "").trim();
  if (!text) return "";
  if (/^(?:1-)?tren$/i.test(text)) return "Тренажерный зал";
  return text;
}

function normalizeRoomKey(label) {
  return normalizeRoomLabel(label).replace(/[\s_]+/g, "").toUpperCase();
}

function buildAuditoryBuildingMembership() {
  const membership = new Map();
  const addRooms = (building, rooms) => {
    (rooms || []).forEach((room) => {
      const key = normalizeRoomKey(room);
      if (!key) return;
      if (!membership.has(key)) membership.set(key, new Set());
      membership.get(key).add(building);
    });
  };

  addRooms("1", AUDITORIES_BUILDING_1);
  addRooms("2", AUDITORIES_BUILDING_2);

  return membership;
}

const AUDITORY_BUILDING_MEMBERSHIP = buildAuditoryBuildingMembership();

function resolveBuildingFromCatalog(roomLabel) {
  const key = normalizeRoomKey(roomLabel);
  if (!key) return null;
  const buildings = AUDITORY_BUILDING_MEMBERSHIP.get(key);
  if (!buildings || buildings.size !== 1) return null;
  return Array.from(buildings)[0];
}

function resolveBuildingFromSource(source) {
  const normalizedSource = normalizeSource(source);
  return SOURCE_BUILDING_FALLBACKS[normalizedSource] || null;
}

function getRoomMeta(item) {
  const normalizedRoom = String(item?.auditory_norm || "").trim();
  const canonical = normalizedRoom.match(/^\s*([12])\s*[-:/]\s*([0-9A-Za-zА-Яа-я]+)\s*$/u);

  let building = null;
  let roomLabel = "";

  if (canonical) {
    building = canonical[1];
    roomLabel = normalizeRoomLabel(canonical[2]);
    const catalogBuilding = resolveBuildingFromCatalog(roomLabel);
    if (catalogBuilding) building = catalogBuilding;
  } else {
    const fallbackRoom = extractRoom(item);
    if (!fallbackRoom) return null;
    roomLabel = normalizeRoomLabel(fallbackRoom);
    building = resolveBuildingFromCatalog(roomLabel);
    if (!building) {
      building = detectBuildingFromRaw(item?.auditory);
    }
    if (!building) {
      building = resolveBuildingFromSource(item?.source);
    }
    if (!building) {
      building = "1";
    }
  }

  if (!roomLabel) return null;
  return {
    building,
    roomLabel,
    key: `${building}-${roomLabel}`,
  };
}

function collectAuditoriesByBuilding(week) {
  const result = { "1": new Map(), "2": new Map() };
  Object.values(week || {}).forEach((dayItems) => {
    (dayItems || []).forEach((item) => {
      const meta = getRoomMeta(item);
      if (!meta) return;
      result[meta.building].set(meta.key, meta.roomLabel);
    });
  });
  return {
    "1": Array.from(result["1"].entries())
      .map(([key, roomLabel]) => ({ key, roomLabel }))
      .sort((a, b) => a.roomLabel.localeCompare(b.roomLabel, "ru", { numeric: true, sensitivity: "base" })),
    "2": Array.from(result["2"].entries())
      .map(([key, roomLabel]) => ({ key, roomLabel }))
      .sort((a, b) => a.roomLabel.localeCompare(b.roomLabel, "ru", { numeric: true, sensitivity: "base" })),
  };
}

function collectAuditoriesByBuildingFromList(auditories) {
  const result = { "1": new Map(), "2": new Map() };
  (auditories || []).forEach((raw) => {
    const text = String(raw || "").trim().replace(/_/g, " ");
    if (!text) return;

    const canonical = text.match(/^\s*([12])\s*[-:/]\s*([0-9A-Za-zА-Яа-я]+)\s*$/u);
    let building = null;
    let roomLabel = "";

    if (canonical) {
      building = canonical[1];
      roomLabel = normalizeRoomLabel(canonical[2]);
    } else {
      const m = text.match(/(\d{2,4})/);
      roomLabel = normalizeRoomLabel(m ? m[1] : text);
      building = resolveBuildingFromCatalog(roomLabel);
      if (!building) {
        building = detectBuildingFromRaw(text);
      }
      if (!building) {
        building = "1";
      }
    }

    if (!roomLabel) return;
    result[building].set(`${building}-${roomLabel}`, roomLabel);
  });

  return {
    "1": Array.from(result["1"].entries())
      .map(([key, roomLabel]) => ({ key, roomLabel }))
      .sort((a, b) => a.roomLabel.localeCompare(b.roomLabel, "ru", { numeric: true, sensitivity: "base" })),
    "2": Array.from(result["2"].entries())
      .map(([key, roomLabel]) => ({ key, roomLabel }))
      .sort((a, b) => a.roomLabel.localeCompare(b.roomLabel, "ru", { numeric: true, sensitivity: "base" })),
  };
}

function mergeAuditoryIndexes(base, extra) {
  const mergeOne = (a = [], b = []) => {
    const byKey = new Map();
    [...a, ...b].forEach((item) => {
      if (!item?.key || !item?.roomLabel) return;
      byKey.set(item.key, item.roomLabel);
    });
    return Array.from(byKey.entries())
      .map(([key, roomLabel]) => ({ key, roomLabel }))
      .sort((x, y) => x.roomLabel.localeCompare(y.roomLabel, "ru", { numeric: true, sensitivity: "base" }));
  };
  return {
    "1": mergeOne(base?.["1"], extra?.["1"]),
    "2": mergeOne(base?.["2"], extra?.["2"]),
  };
}

function flattenWeek(week) {
  const all = [];
  Object.values(week || {}).forEach((dayItems) => {
    (dayItems || []).forEach((item) => all.push(item));
  });
  return all;
}

function normalizeDisciplineTitle(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text
    .replace(/\s+ауд\.?\s*[0-9А-Яа-я/-]+\s*$/u, "")
    .replace(/^Физическая культура\s+тренажерный зал$/iu, "Физическая культура")
    .replace(/^Физическая культура\s+спортивный зал(?:\s*№\s*\d+)?$/iu, "Физическая культура")
    .trim();
}

function summarizeLesson(items) {
  if (!items || items.length === 0) return null;
  const first = items[0];
  const disciplines = [...new Set(items.map((x) => normalizeDisciplineTitle(x.discipline)).filter(Boolean))];
  const teachers = [...new Set(items.map((x) => x.teacher).filter(Boolean))];
  const groups = [...new Set(items.map((x) => x.group_display || x.group).filter(Boolean))];
  const lessonTypes = [
    ...new Set(items.map((x) => x.type_full || x.type).filter((x) => typeof x === "string" && x.trim())),
  ];

  return {
    pairIndex: Number(first.pair_index) || null,
    pairLabel:
      first.pair ||
      (first.pair_index ? `${first.pair_index} пара` : "Пара"),
    timeRange:
      first.time_start && first.time_end
        ? `${first.time_start} - ${first.time_end}`
        : "Время не указано",
    discipline: disciplines.join(", ") || "Не указано",
    teacher: teachers.join(", ") || "Не указан",
    groups: groups.join(", ") || "Не указаны",
    lessonType: lessonTypes.join(", ") || "Не указан",
  };
}

function MetaIcon({ kind }) {
  if (kind === "teacher") {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 21a6 6 0 0 0-12 0" />
        <circle cx="12" cy="8" r="4" />
      </svg>
    );
  }
  if (kind === "auditory") {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 21s-6-4.35-6-10a6 6 0 1 1 12 0c0 5.65-6 10-6 10Z" />
        <circle cx="12" cy="11" r="2.5" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="10" cy="7" r="3" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 4.13a3 3 0 0 1 0 5.74" />
    </svg>
  );
}

const normTeacherKey = (s) =>
  String(s || "").trim().toLowerCase().replace(/\.\s+/g, ".");

function resolveLessonTypeBadge(typeFull) {
  const key = String(typeFull || "").trim().toLowerCase();
  if (key.includes("лек")) return { badge: "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700/50", dot: "bg-amber-400" };
  if (key.includes("практ") || key.includes("семинар")) return { badge: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/50", dot: "bg-emerald-400" };
  if (key.includes("лаб")) return { badge: "bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-700/50", dot: "bg-violet-400" };
  if (key.includes("урок")) return { badge: "bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-700/50", dot: "bg-sky-400" };
  return { badge: "bg-slate-50 dark:bg-slate-700/40 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600/50", dot: "bg-slate-400" };
}

function LessonCardModern({ lesson, tone, auditoryLabel = "", className = "", teacherMap = new Map() }) {
  const isCurrent = tone === "current";
  const [startTimeRaw = "", endTimeRaw = ""] = String(lesson?.timeRange || "")
    .split("-")
    .map((x) => x.trim());
  const startTime = startTimeRaw || lesson?.timeRange || "--:--";
  const endTime = endTimeRaw || "--:--";
  const disciplineText = lesson?.discipline || "Не указано";
  const disciplineLength = disciplineText.length;

  const containerClass = isCurrent
    ? "border-2 border-rose-400 bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(248,250,252,0.78))] shadow-[0_0_0_1px_rgba(244,63,94,0.2),0_32px_72px_-38px_rgba(244,63,94,0.18)] ring-0 backdrop-blur-[22px]"
    : "border-2 border-sky-300 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.92),transparent_28%),radial-gradient(circle_at_top_right,rgba(56,189,248,0.2),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.74),rgba(238,248,255,0.66))] shadow-[0_30px_72px_-40px_rgba(14,116,144,0.3)] ring-1 ring-sky-100/90 backdrop-blur-[22px]";
  const statusClass = isCurrent
    ? "border-white/65 bg-white/55 text-violet-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_10px_28px_-22px_rgba(139,92,246,0.4)] backdrop-blur-[16px]"
    : "border-white/65 bg-white/58 text-slate-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.92),0_10px_28px_-22px_rgba(37,99,235,0.24)] backdrop-blur-[16px]";
  const typeMeta = resolveLessonTypeBadge(lesson?.lessonType);
  const disciplineClass =
    disciplineLength > 95
      ? "text-[1.2rem] sm:text-[1.75rem] leading-[1.04]"
      : disciplineLength > 72
      ? "text-[1.3rem] sm:text-[1.95rem] leading-[1.04]"
      : disciplineLength > 52
      ? "text-[1.45rem] sm:text-[2.15rem] leading-[1.05]"
      : "text-[1.65rem] sm:text-[2.45rem] leading-[1.06]";
  const headerSpacingClass = disciplineLength > 72 ? "space-y-3" : "space-y-4";
  const metaLayoutClass =
    disciplineLength > 72
      ? "flex flex-1 flex-col justify-between gap-4"
      : "flex flex-1 flex-col justify-evenly gap-5";

  const metaRows = [
    { key: "teacher", label: "Преподаватель", value: lesson?.teacher || "Не указано", kind: "teacher" },
    { key: "auditory", label: "Аудитория", value: auditoryLabel || "Не указано", kind: "auditory" },
    { key: "group", label: "Группа", value: lesson?.groups || "Не указано", kind: "group" },
  ];

  if (!lesson) {
    return (
      <div className={`relative h-full w-full min-h-[160px] rounded-[24px] border border-dashed border-slate-300 bg-slate-100/80 dark:border-slate-600/40 dark:bg-slate-800/60 ${className}`}>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
          <img src="/net_par.svg" alt="" className="w-40 h-40 opacity-40 dark:opacity-50 select-none pointer-events-none" draggable={false} />
          <span className="text-center text-sm text-slate-400 dark:text-slate-400 tracking-wide">Нет пар</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`lesson-card-modern${isCurrent ? " lesson-card-modern--current" : ""} relative flex h-full min-h-[240px] flex-col overflow-hidden rounded-[32px] border p-4 sm:rounded-[38px] sm:p-7 ${containerClass} ${className}`}>
      <div className="lesson-card-modern__glows pointer-events-none absolute inset-0">
        <div className="absolute inset-x-5 top-0 h-20 rounded-b-[30px] bg-[linear-gradient(180deg,rgba(255,255,255,0.72),rgba(255,255,255,0.08))] blur-xl" />
        <div className="absolute -left-10 top-16 h-28 w-28 rounded-full bg-white/35 blur-2xl" />
        <div className={`absolute -right-8 top-12 h-32 w-32 rounded-full blur-3xl ${isCurrent ? "bg-violet-200/45" : "bg-sky-200/45"}`} />
        <div className="absolute inset-[1px] rounded-[31px] sm:rounded-[37px] border border-white/55" />
      </div>
      <div className="relative z-10 flex h-full min-h-0 flex-col gap-4 sm:gap-5">
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
            <div className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[0.8rem] font-semibold sm:px-3 sm:py-1.5 sm:text-sm ${statusClass}`}>
              <span className={`h-2.5 w-2.5 rounded-full ${isCurrent ? "bg-violet-500" : "bg-slate-400"}`} />
              <span>{isCurrent ? "Сейчас" : "Выбрано"}</span>
            </div>
            <div className="text-[1rem] font-semibold text-slate-600 sm:text-[1.15rem]">
              {lesson.pairLabel}
            </div>
          </div>

          <div className="relative inline-flex w-full items-center gap-2 overflow-hidden rounded-[26px] border border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.66),rgba(234,244,252,0.46))] px-3 py-2.5 text-slate-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.92),0_18px_36px_-28px_rgba(37,99,235,0.35)] backdrop-blur-[18px] sm:w-auto sm:shrink-0 sm:gap-3 sm:px-4 sm:mr-1">
            <div className="pointer-events-none absolute inset-x-4 top-0 h-8 rounded-b-full bg-white/45 blur-md" />
            <svg viewBox="0 0 24 24" className="relative h-5 w-5 text-sky-600 sm:h-6 sm:w-6" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            <span className="relative font-mono text-[1.15rem] font-semibold tracking-[0.03em] tabular-nums sm:text-[1.95rem]">
              {startTime} - {endTime}
            </span>
          </div>
        </div>

        <div className={headerSpacingClass}>
          <div className={`break-words pl-3 font-semibold text-slate-900 sm:pl-0 ${disciplineClass}`}>
            {disciplineText}
          </div>
          <div className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[0.8rem] font-semibold sm:px-3 sm:py-1.5 sm:text-sm ${typeMeta.badge}`}>
            <svg viewBox="0 0 24 24" className="h-4 w-4 sm:h-[18px] sm:w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="4" y="5" width="16" height="15" rx="2.5" />
              <path d="M8 3v4M16 3v4M8 11h8M8 15h5" />
            </svg>
            <span>{lesson.lessonType || "Не указано"}</span>
          </div>
        </div>

        <div className="h-px w-full bg-[linear-gradient(90deg,rgba(255,255,255,0.05),rgba(148,163,184,0.35),rgba(255,255,255,0.05))]" />

        <div className={metaLayoutClass}>
          {metaRows.map((row) => {
            const tid = row.key === "teacher" && row.value !== "Не указано"
              ? teacherMap.get(normTeacherKey(row.value))
              : null;
            return (
              <div key={row.key} className="grid grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-x-3 gap-y-1 sm:grid-cols-[1.5rem_minmax(0,1fr)] sm:gap-x-4">
                <div className="pt-1 text-sky-600">
                  <MetaIcon kind={row.kind} />
                </div>
                <div className="min-w-0">
                  <div className="text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-slate-400 sm:text-[0.92rem]">
                    {row.label}
                  </div>
                  <div className="mt-1 break-words text-[0.95rem] font-semibold leading-snug text-slate-900 sm:text-[1.18rem]">
                    {tid ? (
                      <Link
                        to={`/profile/${tid}`}
                        className="text-[#3b82f6] hover:text-[#1e3a8a] hover:underline underline-offset-2 transition-colors"
                      >
                        {row.value}
                      </Link>
                    ) : row.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function AuditorySearch() {
  console.log("AuditorySearch component mounted - NEW BUILD");
  const [teacherMap, setTeacherMap] = useState(new Map());
  const [week, setWeek] = useState({});
  const [weekByRange, setWeekByRange] = useState({});
  const [liveWeekDates, setLiveWeekDates] = useState([]);
  const [archiveRanges, setArchiveRanges] = useState([]);
  const [catalogAuditoriesByBuilding, setCatalogAuditoriesByBuilding] = useState({ "1": [], "2": [] });
  const [archiveLoaded, setArchiveLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading) window.dispatchEvent(new CustomEvent("pageContentReady"));
  }, [loading]);
  const [selectedAuditory, setSelectedAuditory] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(SELECTED_AUDITORY_CACHE_KEY) || "";
  });
  const [selectedBuilding, setSelectedBuilding] = useState(() => {
    if (typeof window === "undefined") return "1";
    return localStorage.getItem(SELECTED_BUILDING_CACHE_KEY) || "1";
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    if (typeof window === "undefined") return "";
    return toLocalIsoDate(new Date());
  });
  const [selectedPairKey, setSelectedPairKey] = useState("");
  const [pairFilter, setPairFilter] = useState(null);
  const [pairFilterOpen, setPairFilterOpen] = useState(false);
  const [teacherQuery, setTeacherQuery] = useState("");
  const [teacherSearchLoading, setTeacherSearchLoading] = useState(false);
  const [teacherSearchError, setTeacherSearchError] = useState("");
  const [teacherSearchResult, setTeacherSearchResult] = useState(null);
  const [teachersCatalog, setTeachersCatalog] = useState([]);
  const [showTeacherSuggestions, setShowTeacherSuggestions] = useState(false);
  const [pairListFade, setPairListFade] = useState({ top: false, bottom: false });
  const dateButtonRefs = useRef(new Map());
  const dateRulerRef = useRef(null);
  const calendarInputRef = useRef(null);
  const pairListRef = useRef(null);
  const pairItemRefs = useRef(new Map());
  const teacherDialogRef = useRef(null);
  const teacherBlurTimerRef = useRef(null);
  const wheelVelocityRef = useRef(0);
  const wheelAnimRef = useRef(0);
  const userPickedDateRef = useRef(false);
  const referenceNow = useMemo(() => getReferenceNow(), []);
  const todayIso = useMemo(() => toLocalIsoDate(referenceNow.date), [referenceNow]);
  const persistSelectedDate = (date) => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(SELECTED_DATE_CACHE_KEY, date);
    } catch (_e) {
      // ignore storage write errors
    }
  };

  const persistSelectedBuilding = (building) => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(SELECTED_BUILDING_CACHE_KEY, building);
    } catch (_e) {
      // ignore storage write errors
    }
  };

  const persistSelectedAuditory = (auditory) => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(SELECTED_AUDITORY_CACHE_KEY, auditory);
    } catch (_e) {
      // ignore storage write errors
    }
  };

  useEffect(() => {
    fetch("/auth/public/teachers")
      .then((r) => r.ok ? r.json() : [])
      .then((teachers) => {
        const map = new Map();
        teachers.forEach((t) => {
          const fio = String(t.fio || "").trim();
          if (!fio) return;
          map.set(normTeacherKey(fio), t.id);
          const parts = fio.split(/\s+/);
          if (parts.length >= 2 && !/\.$/.test(parts[1])) {
            const abbr = parts[0] + " " + parts.slice(1).map((p) => p[0] + ".").join("");
            const k = normTeacherKey(abbr);
            if (!map.has(k)) map.set(k, t.id);
          }
        });
        setTeacherMap(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadArchiveList() {
      setLoading(true);
      setError("");
      try {
        const cacheKey = "auditories:archive-list:all";
        const cached = peekCachedJson(cacheKey, 5 * 60_000);
        if (cached) {
          if (cancelled) return;
          const ranges = Array.isArray(cached) ? cached : [];
          console.debug("auditories: archive list (cached)", ranges.length, ranges.slice(0,3));
          setArchiveRanges(ranges);
          return;
        }

        const data = await fetchJsonCached(`${window.location.origin}/schedule/archive-list?source=all`, {
          cacheKey,
          ttlMs: 5 * 60_000,
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        });
        if (cancelled) return;
        const ranges = Array.isArray(data) ? data : [];
        console.debug("auditories: archive list (fetched)", ranges.length, ranges.slice(0,3));
        setArchiveRanges(ranges);
        try {
          if (typeof window !== "undefined") {
            fetch(`${window.location.origin}/debug/client-log`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ kind: "archive-list", count: ranges.length, sample: ranges.slice(0, 5) }),
            }).catch(() => {});
          }
        } catch (e) {
          /* ignore */
        }
      } catch (e) {
        if (cancelled) return;
        setArchiveRanges([]);
        setError(e?.message || "Не удалось загрузить список архивов");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setArchiveLoaded(true);
        }
      }
    }

    loadArchiveList();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch live week dates on mount so next week is visible in timeline on Sundays
  useEffect(() => {
    let cancelled = false;
    async function fetchLiveWeekDates() {
      try {
        const data = await fetchJsonCached(`${window.location.origin}/schedule/week?source=all`, {
          cacheKey: "auditories:schedule:all:current",
          ttlMs: 2 * 60_000,
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        });
        if (cancelled || !data?.week) return;
        const dates = Object.values(data.week).flat().map((item) => item?.date).filter(Boolean);
        setLiveWeekDates([...new Set(dates)]);
      } catch {}
    }
    fetchLiveWeekDates();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadAuditoryCatalog() {
      try {
        const data = await fetchJsonCached(`${window.location.origin}/api/auditories`, {
          cacheKey: "auditories:catalog",
          ttlMs: 10 * 60_000,
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        });
        if (cancelled) return;
        const list = Array.isArray(data?.auditories) ? data.auditories : [];
        // Use fetched catalog as informative only; authoritative source is constants below.
        try {
          // keep fetched value in debug log
          console.debug("auditories: fetched catalog count", Array.isArray(list) ? list.length : 0);
        } catch (e) {}
      } catch (_e) {
        // fallback to built-in constants if catalog endpoint unavailable
        try {
          // ignore
        } catch (ee) {
          // ignore
        }
      }
      // Enforce authoritative constants for auditoriums UI
      try {
        const pref1 = (AUDITORIES_BUILDING_1 || []).map((r) => `1-${r}`);
        const pref2 = (AUDITORIES_BUILDING_2 || []).map((r) => `2-${r}`);
        setCatalogAuditoriesByBuilding(collectAuditoriesByBuildingFromList([...pref1, ...pref2]));
      } catch (e) {
        // ignore
      }
    }
    loadAuditoryCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  const auditoriesByBuilding = useMemo(
    () => mergeAuditoryIndexes(catalogAuditoriesByBuilding, collectAuditoriesByBuilding(week)),
    [catalogAuditoriesByBuilding, week]
  );
  const auditories = useMemo(
    () => auditoriesByBuilding[selectedBuilding] || EMPTY_AUDITORIES,
    [auditoriesByBuilding, selectedBuilding]
  );
  const dayAuditoryKeys = useMemo(() => {
    const keys = new Set();
    flattenWeek(week)
      .filter((item) => item?.date === selectedDate)
      .forEach((item) => {
        const meta = getRoomMeta(item);
        if (!meta) return;
        if (meta.building !== selectedBuilding) return;
        keys.add(meta.key);
      });
    return Array.from(keys);
  }, [week, selectedDate, selectedBuilding]);
  const dateToRangeIds = useMemo(() => {
    const mapping = new Map();
    archiveRanges.forEach((range) => {
      const id = String(range?.id || "").trim();
      const from = String(range?.from || "").trim();
      const to = String(range?.to || "").trim();
      if (!id || !from || !to) return;
      expandIsoDateRange(from, to).forEach((date) => {
        if (!mapping.has(date)) mapping.set(date, []);
        mapping.get(date).push(id);
      });
    });
    mapping.forEach((ids, date) => {
      mapping.set(date, Array.from(new Set(ids)));
    });
    return mapping;
  }, [archiveRanges]);

  const availableDates = useMemo(() => {
    // Build set of available dates from archive ranges and current week
    const unique = new Set();
    Array.from(dateToRangeIds.keys()).forEach((date) => {
      if (date) unique.add(date);
    });
    flattenWeek(week).forEach((item) => {
      if (item?.date) unique.add(item.date);
    });
    liveWeekDates.forEach((d) => { if (d) unique.add(d); });

    // If there are archive ranges, expand to continuous span from earliest start to latest end
    console.debug("auditories: archiveRanges.length =", archiveRanges?.length || 0);
    if (archiveRanges && archiveRanges.length) {
      let minDate = null;
      let maxDate = null;
      archiveRanges.forEach((r) => {
        const from = String(r?.from || "").trim();
        const to = String(r?.to || "").trim();
        if (!from || !to) return;
        if (!minDate || from < minDate) minDate = from;
        if (!maxDate || to > maxDate) maxDate = to;
      });
      console.debug("auditories: minDate/maxDate =", minDate, maxDate);
      if (minDate && maxDate) {
        expandIsoDateRange(minDate, maxDate).forEach((d) => unique.add(d));
      }
    }

    const out = Array.from(unique)
      .filter((date) => !isSunday(date))
      .sort();
    // debug info
    try {
      if (typeof window !== "undefined") {
        console.debug("auditories: availableDates", out.length, "min->max", out[0], out[out.length - 1]);
        try {
          fetch(`${window.location.origin}/debug/client-log`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ kind: "available-dates", length: out.length, min: out[0], max: out[out.length - 1] }),
          }).catch(() => {});
        } catch (e) {
          /* ignore */
        }
      }
    } catch (e) {}
    return out;
  }, [dateToRangeIds, week, archiveRanges, liveWeekDates]);

  const timelineDates = useMemo(() => {
    if (!selectedDate) return availableDates;
    if (availableDates.includes(selectedDate)) return availableDates;
    return [...availableDates, selectedDate].sort();
  }, [availableDates, selectedDate]);

  const edgePadCount = 5;
  const timelineItems = useMemo(() => {
    const left = Array.from({ length: edgePadCount }, (_, i) => ({ kind: "pad", key: `pad-left-${i}` }));
    const center = timelineDates.map((date) => ({ kind: "date", key: date, date }));
    const right = Array.from({ length: edgePadCount }, (_, i) => ({ kind: "pad", key: `pad-right-${i}` }));
    return [...left, ...center, ...right];
  }, [timelineDates]);

  useEffect(() => {
    if (!archiveLoaded) return;

    if (!availableDates.length) {
      setSelectedDate((prev) => prev || todayIso);
      return;
    }

    setSelectedDate((prev) => {
      // Keep any selected date that exists in available dates (past or future)
      if (prev && availableDates.includes(prev)) return prev;
      // Keep today even if no classes — shows "нет пар" which is correct
      if (prev === todayIso) return prev;
      // Default to today if available
      if (availableDates.includes(todayIso)) return todayIso;
      // Today not in list — prefer next available date (future), fall back to nearest
      const futureDate = availableDates.find((d) => d > todayIso);
      if (futureDate) return futureDate;
      return findNearestDate(todayIso, availableDates);
    });
  }, [availableDates, todayIso, archiveLoaded]);

  useEffect(() => {
    let cancelled = false;

    async function loadDaySchedule() {
      if (!selectedDate) return;
      const rangeIds = dateToRangeIds.get(selectedDate) || [];
      const missing = rangeIds.filter((id) => !weekByRange[id]);

      setLoading(true);
      setError("");
      try {
        let loadedMap = {};

        if (missing.length) {
          const loadedPairs = await Promise.all(
            missing.map(async (rangeId) => {
              const data = await fetchJsonCached(
                `${window.location.origin}/schedule/week?source=all&range_id=${encodeURIComponent(rangeId)}`,
                {
                  cacheKey: `auditories:schedule:all:${rangeId}`,
                  ttlMs: 2 * 60_000,
                  headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeaders(),
                  },
                }
              );
              return [rangeId, data?.week || {}];
            })
          );
          loadedMap = Object.fromEntries(loadedPairs);
          if (!cancelled) {
            setWeekByRange((prev) => ({ ...prev, ...loadedMap }));
          }
        }

        if (cancelled) return;
        let allWeekChunks = rangeIds.map((id) => weekByRange[id] || loadedMap[id]).filter(Boolean);
        if (!allWeekChunks.length) {
          const data = await fetchJsonCached(`${window.location.origin}/schedule/week?source=all`, {
            cacheKey: "auditories:schedule:all:current",
            ttlMs: 2 * 60_000,
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeaders(),
            },
          });
          if (data) {
            const currentWeek = data?.week || {};
            setWeek(currentWeek);
            return;
          }
        }
        const mergedDay = [];
        const seen = new Set();

        allWeekChunks.forEach((wk) => {
          Object.values(wk || {})
            .flat()
            .forEach((item) => {
              if (item?.date !== selectedDate) return;
              const signature = [
                item?.date || "",
                item?.pair_index || "",
                item?.pair || "",
                item?.discipline || "",
                item?.teacher || "",
                item?.group_key || item?.group || "",
                item?.auditory_norm || item?.auditory || "",
                item?.source || "",
              ].join("|");
              if (seen.has(signature)) return;
              seen.add(signature);
              mergedDay.push(item);
            });
        });

        setWeek(mergedDay.length ? { [selectedDate]: mergedDay } : {});
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || "Не удалось загрузить расписание");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDaySchedule();
    return () => {
      cancelled = true;
    };
  }, [selectedDate, dateToRangeIds, weekByRange]);

  // Prefetch nearby archive weeks to make picker navigation smooth
  useEffect(() => {
    if (!archiveLoaded || !selectedDate) return;
    let cancelled = false;
    const prefetchWindow = 2; // number of neighbour weeks on each side
    const dates = timelineDates || [];
    const idx = dates.indexOf(selectedDate);
    if (idx === -1) return;

    const toPrefetchDates = [];
    for (let offset = -prefetchWindow; offset <= prefetchWindow; offset += 1) {
      if (offset === 0) continue;
      const d = dates[idx + offset];
      if (d) toPrefetchDates.push(d);
    }

    const rangeIdsSet = new Set();
    toPrefetchDates.forEach((d) => {
      (dateToRangeIds.get(d) || []).forEach((id) => rangeIdsSet.add(id));
    });

    const missing = Array.from(rangeIdsSet).filter((id) => !weekByRange[id]);
    if (!missing.length) return;

    (async () => {
      try {
        const loaded = await Promise.all(
          missing.map(async (rangeId) => {
            const data = await fetchJsonCached(
              `${window.location.origin}/schedule/week?source=all&range_id=${encodeURIComponent(rangeId)}`,
              {
                cacheKey: `auditories:schedule:all:${rangeId}`,
                ttlMs: 2 * 60_000,
                headers: {
                  "Content-Type": "application/json",
                  ...getAuthHeaders(),
                },
              }
            );
            return [rangeId, data?.week || {}];
          })
        );
        if (cancelled) return;
        setWeekByRange((prev) => ({ ...prev, ...Object.fromEntries(loaded) }));
      } catch (_e) {
        // ignore prefetch errors
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [archiveLoaded, selectedDate, timelineDates, dateToRangeIds, weekByRange]);

  useEffect(() => {
    const container = dateRulerRef.current;
    const target = dateButtonRefs.current.get(selectedDate);
    if (!container || !target) return;
    const frameId = window.requestAnimationFrame(() => {
      scrollContainerToChild(container, target, { axis: "x", behavior: "smooth", align: "center" });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [selectedDate, timelineDates]);


  const handleCalendarDateChange = (event) => {
    const picked = String(event.target.value || "").trim();
    if (!picked || !availableDates.length) return;
    userPickedDateRef.current = true;
    setSelectedDate(findNearestDate(picked, availableDates));
  };


  useEffect(() => {
    if (!selectedBuilding) return;
    persistSelectedBuilding(selectedBuilding);
  }, [selectedBuilding]);

  useEffect(() => {
    persistSelectedAuditory(selectedAuditory || "");
  }, [selectedAuditory]);

  const handleDateRulerWheel = useCallback((event) => {
    const track = dateRulerRef.current;
    if (!track) return;

    const delta = Math.abs(event.deltaY) > Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
    event.preventDefault();
    event.stopPropagation();
    if (!delta) return;
    if (track.scrollWidth > track.clientWidth) {
      wheelVelocityRef.current += delta * 0.45;

      if (!wheelAnimRef.current) {
        const tick = () => {
          const el = dateRulerRef.current;
          if (!el) {
            wheelAnimRef.current = 0;
            return;
          }

          const v = wheelVelocityRef.current;

          if (Math.abs(v) < 0.2) {
            wheelVelocityRef.current = 0;
            wheelAnimRef.current = 0;
            return;
          }

          el.scrollLeft += v;
          wheelVelocityRef.current *= 0.84;
          wheelAnimRef.current = requestAnimationFrame(tick);
        };
        wheelAnimRef.current = requestAnimationFrame(tick);
      }
    }
  }, [dateRulerRef]);

  useEffect(() => {
    return () => {
      if (wheelAnimRef.current) {
        cancelAnimationFrame(wheelAnimRef.current);
        wheelAnimRef.current = 0;
      }
    };
  }, []);

  // Attach native wheel listener with passive: false to allow preventDefault
  useEffect(() => {
    const el = dateRulerRef.current;
    if (!el) return undefined;
    const opts = { passive: false };
    el.addEventListener("wheel", handleDateRulerWheel, opts);
    return () => el.removeEventListener("wheel", handleDateRulerWheel, opts);
  }, [dateRulerRef, handleDateRulerWheel]);

  const showLiveOccupancy = selectedDate === todayIso;

  const occupiedAuditories = useMemo(() => {
    const busy = new Set();
    if (!selectedDate || !showLiveOccupancy) return busy;

    const nowMinutes = referenceNow.date.getHours() * 60 + referenceNow.date.getMinutes();

    flattenWeek(week)
      .filter((item) => item.date === selectedDate)
      .forEach((item) => {
        const start = parseMinutes(item?.time_start);
        const end = parseMinutes(item?.time_end);
        if (start == null || end == null || nowMinutes < start || nowMinutes > end) return;
        const meta = getRoomMeta(item);
        if (!meta) return;
        busy.add(meta.key);
      });

    return busy;
  }, [week, selectedDate, showLiveOccupancy, referenceNow]);

  useEffect(() => {
    if (!auditories.length) {
      setSelectedAuditory("");
      return;
    }
    const allowed = new Set(auditories.map((a) => a.key));
    setSelectedAuditory((prev) => {
      if (prev && allowed.has(prev)) return prev;
      const preferredWithLessons = dayAuditoryKeys.find((k) => allowed.has(k));
      if (preferredWithLessons) return preferredWithLessons;
      return auditories[0].key;
    });
  }, [auditories, dayAuditoryKeys]);

  useEffect(() => { setPairFilter(null); setPairFilterOpen(false); }, [selectedDate]);

  useEffect(() => {
    if (!pairFilterOpen) return undefined;
    const handler = (e) => { if (e.key === "Escape") setPairFilterOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [pairFilterOpen]);

  // Уникальные пары на выбранную дату (для кнопок фильтра)
  const pairsOnDate = useMemo(() => {
    const map = new Map();
    flattenWeek(week)
      .filter((item) => item?.date === selectedDate)
      .forEach((item) => {
        const idx = Number(item?.pair_index);
        if (!idx) return;
        if (!map.has(idx)) {
          map.set(idx, {
            index: idx,
            label: item.pair || `${idx} пара`,
            timeStart: item.time_start || "",
            timeEnd: item.time_end || "",
          });
        }
      });
    return Array.from(map.values()).sort((a, b) => a.index - b.index);
  }, [week, selectedDate]);

  // Аудитории, занятые в выбранную пару
  const busyForPair = useMemo(() => {
    if (!pairFilter) return null;
    const busy = new Set();
    flattenWeek(week)
      .filter((item) => item?.date === selectedDate && Number(item?.pair_index) === pairFilter)
      .forEach((item) => {
        const meta = getRoomMeta(item);
        if (meta) busy.add(meta.key);
      });
    return busy;
  }, [week, selectedDate, pairFilter]);

  const freeCountByPair = useMemo(() => {
    const allKeys = new Set(auditories.map((a) => a.key));
    const result = new Map();
    pairsOnDate.forEach((p) => {
      const busy = new Set();
      flattenWeek(week)
        .filter((item) => item?.date === selectedDate && Number(item?.pair_index) === p.index)
        .forEach((item) => {
          const meta = getRoomMeta(item);
          if (meta && allKeys.has(meta.key)) busy.add(meta.key);
        });
      result.set(p.index, Math.max(0, allKeys.size - busy.size));
    });
    return result;
  }, [week, selectedDate, auditories, pairsOnDate]);

  const lessonsForSelectedDate = useMemo(() => {
    if (!selectedAuditory || !selectedDate) return [];
    return flattenWeek(week)
      .filter((item) => getRoomMeta(item)?.key === selectedAuditory)
      .filter((item) => item.date === selectedDate)
      .sort((a, b) => {
        const ai = Number(a?.pair_index) || 0;
        const bi = Number(b?.pair_index) || 0;
        if (ai !== bi) return ai - bi;
        const am = parseMinutes(a?.time_start) || 0;
        const bm = parseMinutes(b?.time_start) || 0;
        return am - bm;
      });
  }, [week, selectedAuditory, selectedDate]);

  const dayPairBlocks = useMemo(() => {
    if (!lessonsForSelectedDate.length) return [];
    const groups = new Map();
    lessonsForSelectedDate.forEach((item) => {
      const idx = Number(item?.pair_index) || null;
      const key = idx !== null && idx !== 0 ? idx : String(item?.pair || item?.pair_index || "");
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });

    const entries = Array.from(groups.entries()).map(([k, items]) => ({ k, items }));
    entries.sort((a, b) => {
      const an = Number(a.k) || 0;
      const bn = Number(b.k) || 0;
      return an - bn;
    });

    return entries.map(({ items }) => summarizeLesson(items) || {});
  }, [lessonsForSelectedDate]);

  const dayPairBlocksWithGaps = useMemo(() => {
    if (!dayPairBlocks.length) return [];
    const numeric = dayPairBlocks.map((b) => Number(b.pairIndex)).filter((n) => Number.isFinite(n));
    if (!numeric.length) return dayPairBlocks;
    const min = Math.min(...numeric);
    const max = Math.max(...numeric);
    const byIndex = new Map();
    dayPairBlocks.forEach((b) => {
      if (b && Number.isFinite(Number(b.pairIndex))) byIndex.set(Number(b.pairIndex), b);
    });
    const out = [];
    for (let i = min; i <= max; i += 1) {
      if (byIndex.has(i)) out.push(byIndex.get(i));
      else
        out.push({
          pairIndex: i,
          pairLabel: `${i} пара`,
          timeRange: "",
          discipline: "",
          teacher: "",
          groups: "",
          lessonType: "",
          isGap: true,
        });
    }
    return out;
  }, [dayPairBlocks]);
  const currentPairKey = useMemo(() => {
    if (!selectedAuditory || selectedDate !== todayIso || !dayPairBlocks.length) return "";
    const nowMinutes = referenceNow.date.getHours() * 60 + referenceNow.date.getMinutes();

    for (const block of dayPairBlocks) {
      const src = lessonsForSelectedDate.find((x) => (Number(x.pair_index) || 0) === (block.pairIndex || 0));
      const start = parseMinutes(src?.time_start);
      const end = parseMinutes(src?.time_end);
      if (start != null && end != null && nowMinutes >= start && nowMinutes <= end) {
        return String(block.pairIndex || block.pairLabel || "");
      }
    }
    return "";
  }, [selectedAuditory, selectedDate, todayIso, dayPairBlocks, lessonsForSelectedDate, referenceNow]);

  const refreshPairListFade = () => {
    const container = pairListRef.current;
    if (!container) {
      setPairListFade({ top: false, bottom: false });
      return;
    }
    const top = container.scrollTop > 6;
    const bottom = container.scrollTop + container.clientHeight < container.scrollHeight - 6;
    setPairListFade({ top, bottom });
  };

  useEffect(() => {
    if (!dayPairBlocks.length) {
      setSelectedPairKey("");
      return;
    }
    const keys = new Set(dayPairBlocks.map((x) => String(x.pairIndex || x.pairLabel || "")));
    setSelectedPairKey((prev) => {
      if (prev && keys.has(prev)) return prev;
      if (currentPairKey && keys.has(currentPairKey)) return currentPairKey;
      return String(dayPairBlocks[0].pairIndex || dayPairBlocks[0].pairLabel || "");
    });
  }, [dayPairBlocks, currentPairKey]);

  useEffect(() => {
    if (!currentPairKey) return;
    const container = pairListRef.current;
    const item = pairItemRefs.current.get(String(currentPairKey));
    if (!container || !item) return;

    scrollContainerToChild(container, item, { axis: "y", behavior: "smooth", align: "center" });
  }, [currentPairKey, selectedAuditory, selectedDate, dayPairBlocksWithGaps.length]);

  useEffect(() => {
    refreshPairListFade();
    const container = pairListRef.current;
    if (!container) return undefined;

    const handleScroll = () => refreshPairListFade();
    let resizeRaf = null;
    const handleResize = () => {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(() => { resizeRaf = null; refreshPairListFade(); });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
    };
  }, [selectedAuditory, selectedDate, dayPairBlocksWithGaps.length]);

  const selectedLesson = useMemo(
    () => dayPairBlocks.find((x) => String(x.pairIndex || x.pairLabel || "") === selectedPairKey) || null,
    [dayPairBlocks, selectedPairKey]
  );
  const mobilePickerSelectedIndex = useMemo(
    () =>
      dayPairBlocksWithGaps.findIndex(
        (x) => !x?.isGap && String(x.pairIndex || x.pairLabel || "") === selectedPairKey
      ),
    [dayPairBlocksWithGaps, selectedPairKey]
  );
  const mobilePickerIdBase = useId().replace(/:/g, "");
  const mobilePickerHeight = useMemo(() => {
    if (!dayPairBlocksWithGaps.length) return 0;
    const itemsHeight = dayPairBlocksWithGaps.reduce(
      (sum, item) => sum + (item.isGap ? MOBILE_PICKER_GAP_ITEM_HEIGHT : MOBILE_PICKER_BUTTON_SIZE),
      0
    );
    return MOBILE_PICKER_PADDING * 2 + itemsHeight + Math.max(0, dayPairBlocksWithGaps.length - 1) * MOBILE_PICKER_GAP;
  }, [dayPairBlocksWithGaps]);

  const mobilePickerPath = useMemo(
    () => buildMobilePickerPath(mobilePickerHeight, null),
    [mobilePickerHeight]
  );
  const prevBlobIdxRef = useRef(-1);
  const [blobPos, setBlobPos] = useState(() => ({
    top: 0, height: MOBILE_PICKER_BUTTON_SIZE, radius: '50%', transition: 'none',
  }));
  useEffect(() => {
    const prevIdx = prevBlobIdxRef.current;
    const currIdx = mobilePickerSelectedIndex;
    prevBlobIdxRef.current = currIdx;
    if (currIdx < 0) return;
    const getTopForIndex = (idx) => {
      let top = 0;
      for (let i = 0; i < idx; i++) {
        top += (dayPairBlocksWithGaps[i]?.isGap ? MOBILE_PICKER_GAP_ITEM_HEIGHT : MOBILE_PICKER_BUTTON_SIZE) + MOBILE_PICKER_GAP;
      }
      return top;
    };
    const currTop = getTopForIndex(currIdx);
    if (prevIdx < 0 || prevIdx === currIdx) {
      setBlobPos({ top: currTop, height: MOBILE_PICKER_BUTTON_SIZE, radius: '50%', transition: 'none' });
      return;
    }
    const prevTop = getTopForIndex(prevIdx);
    setBlobPos({
      top: Math.min(prevTop, currTop),
      height: Math.abs(currTop - prevTop) + MOBILE_PICKER_BUTTON_SIZE,
      radius: '24px',
      transition: 'none',
    });
    const timer = setTimeout(() => {
      setBlobPos({
        top: currTop,
        height: MOBILE_PICKER_BUTTON_SIZE,
        radius: '50%',
        transition: [
          'top 340ms cubic-bezier(0.34,1.56,0.64,1)',
          'height 340ms cubic-bezier(0.34,1.56,0.64,1)',
          'border-radius 200ms ease-out',
        ].join(', '),
      });
    }, 20);
    return () => clearTimeout(timer);
  }, [mobilePickerSelectedIndex, dayPairBlocksWithGaps]);

const selectedAuditoryDisplay = useMemo(() => {
    const all = [...(auditoriesByBuilding["1"] || []), ...(auditoriesByBuilding["2"] || [])];
    const found = all.find((a) => a.key === selectedAuditory);
    if (!found) return "";
    return found.roomLabel;
  }, [auditoriesByBuilding, selectedAuditory]);

  const teacherSearchNowLabel = useMemo(
    () =>
      new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      }).format(referenceNow.date),
    [referenceNow]
  );

  const openTeacherDialog = () => {
    const dlg = teacherDialogRef.current;
    if (!dlg) return;
    if (!dlg.open && typeof dlg.showModal === "function") {
      dlg.showModal();
      setTimeout(() => window.dispatchEvent(new CustomEvent("pageContentReady")), 80);
    }
  };

  const closeTeacherDialog = () => {
    const dlg = teacherDialogRef.current;
    if (!dlg) return;
    setShowTeacherSuggestions(false);
    if (typeof dlg.close === "function") {
      dlg.close();
    }
  };

  const normalizeSearch = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/ё/g, "е")
      .replace(/\s+/g, " ")
      .trim();

  useEffect(() => {
    let cancelled = false;

    async function loadTeachersCatalog() {
      try {
        const data = await fetchJsonCached(`${window.location.origin}/export/teachers?source=all`, {
          cacheKey: "auditories:teachers:all",
          ttlMs: 10 * 60_000,
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        });
        if (cancelled) return;
        setTeachersCatalog(Array.isArray(data) ? data : []);
      } catch (_e) {
        if (!cancelled) setTeachersCatalog([]);
      }
    }

    loadTeachersCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (teacherBlurTimerRef.current) {
        clearTimeout(teacherBlurTimerRef.current);
        teacherBlurTimerRef.current = null;
      }
    };
  }, []);

  const filteredTeacherSuggestions = useMemo(() => {
    const q = normalizeSearch(teacherQuery);
    if (!q || q.length < 2) return [];
    return teachersCatalog
      .filter((name) => normalizeSearch(name).includes(q))
      .slice(0, 12);
  }, [teacherQuery, teachersCatalog]);

  const parseDateSafe = (isoDate) => {
    const d = parseIsoDate(isoDate);
    return d ? d.getTime() : Number.POSITIVE_INFINITY;
  };

  const parseAuditoryLocation = (rawValue) => {
    const raw = String(rawValue || "").trim().replace(/_/g, " ");
    if (!raw) return { room: "", building: "" };

    const canonical = raw.match(/^\s*([12])\s*[-:/]\s*(.+?)\s*$/);
    if (canonical) {
      return { room: canonical[2].trim(), building: canonical[1] };
    }

    let building = "";
    if (/\bук\s*№?\s*1\b|\bкорпус\s*1\b/i.test(raw)) building = "1";
    if (/\bук\s*№?\s*2\b|\bкорпус\s*2\b/i.test(raw)) building = "2";

    const roomMatches = [...raw.matchAll(/(\d{2,4}[А-Яа-яA-Za-z]?(?:\/\d{1,4})?)/g)];
    const room = roomMatches.length ? roomMatches[roomMatches.length - 1][1] : raw;
    return { room: String(room || "").trim(), building };
  };

  const handleTeacherSearch = async (event) => {
    event.preventDefault();
    setShowTeacherSuggestions(false);
    const query = normalizeSearch(teacherQuery);
    if (!query) {
      setTeacherSearchError("Введите фамилию преподавателя");
      setTeacherSearchResult(null);
      return;
    }

    setTeacherSearchLoading(true);
    setTeacherSearchError("");
    setTeacherSearchResult(null);

    try {
      const res = await fetch(`${window.location.origin}/schedule/week?source=all`, {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Ошибка ${res.status}: ${txt}`);
      }

      const data = await res.json();
      const allLessons = flattenWeek(data?.week || {});
      const matched = allLessons.filter((item) =>
        normalizeSearch(item?.teacher || "").includes(query)
      );

      if (!matched.length) {
        setTeacherSearchError("Пары этого преподавателя не найдены");
        return;
      }

      const now = referenceNow.date;
      const nowMinutes = now.getHours() * 60 + now.getMinutes();

      const normalized = matched
        .map((item) => {
          const auditoryRaw = item?.auditory_norm || extractRoom(item) || item?.auditory || "";
          const location = parseAuditoryLocation(auditoryRaw);
          return {
            date: String(item?.date || "").trim(),
            time_start: String(item?.time_start || "").trim(),
            time_end: String(item?.time_end || "").trim(),
            auditory: location.room,
            building: location.building,
            group: item?.group || "",
            discipline: item?.discipline || "",
            teacher: item?.teacher || "",
          };
        })
        .filter((item) => item.date && item.time_start);

      normalized.sort((a, b) => {
        const da = parseDateSafe(a.date);
        const db = parseDateSafe(b.date);
        if (da !== db) return da - db;
        return (parseMinutes(a.time_start) || 0) - (parseMinutes(b.time_start) || 0);
      });

      const current = normalized.find((lesson) => {
        if (lesson.date !== todayIso) return false;
        const start = parseMinutes(lesson.time_start);
        const end = parseMinutes(lesson.time_end);
        return start != null && end != null && nowMinutes >= start && nowMinutes <= end;
      }) || null;

      let next = null;
      for (const lesson of normalized) {
        const lessonDate = parseDateSafe(lesson.date);
        const todayDate = parseDateSafe(todayIso);
        const start = parseMinutes(lesson.time_start);
        if (lessonDate === todayDate) {
          if (start != null && start > nowMinutes) {
            next = lesson;
            break;
          }
          continue;
        }
        if (lessonDate > todayDate) {
          next = lesson;
          break;
        }
      }

      setTeacherSearchResult({
        query: teacherQuery.trim(),
        current,
        next,
      });
    } catch (e) {
      setTeacherSearchError(e?.message || "Не удалось выполнить поиск");
    } finally {
      setTeacherSearchLoading(false);
    }
  };

  return (
    <>
      <div className="auditories-shell space-y-4 sm:space-y-5">
        <section className="rounded-3xl border border-[#8fb5ea] bg-gradient-to-b from-[#f4f9ff] to-[#e7f1ff] shadow-[0_14px_30px_-20px_rgba(29,78,216,0.42)] p-3 sm:p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div />
            <div className="shrink-0 relative"> {/* Добавили relative */}
            <button
              type="button"
              // На iOS вызываем встроенный метод выбора даты
              onClick={() => {
                if (calendarInputRef.current) {
                  try {
                    // Это стандартный способ для современных браузеров и iOS
                    calendarInputRef.current.showPicker();
                  } catch (e) {
                    // Фолбэк для старых версий
                    calendarInputRef.current.focus();
                    calendarInputRef.current.click();
                  }
                }
              }}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 transition hover:border-blue-400 hover:text-blue-600"
              title="Выбрать дату в календаре"
              aria-label="Выбрать дату в календаре"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="shrink-0">
                <rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7" />
                <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="1.7" />
                <line x1="8" y1="3" x2="8" y2="7" stroke="currentColor" strokeWidth="1.7" />
                <line x1="16" y1="3" x2="16" y2="7" stroke="currentColor" strokeWidth="1.7" />
              </svg>
            </button>

            <input
              ref={calendarInputRef}
              type="date"
              value={selectedDate || ""}
              min={availableDates[0] || undefined}
              max={availableDates[availableDates.length - 1] || undefined}
              onChange={handleCalendarDateChange}
              // ЗАМЕНА sr-only на абсолютное позиционирование поверх кнопки
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              style={{ 
                  fontSize: '16px', // Предотвращает авто-зум в Safari
                  WebkitAppearance: 'none' // Убирает системные тени iOS
              }}
              tabIndex={-1}
              aria-hidden="true"
            />
          </div>
          </div>
          {timelineDates.length ? (
            <div className="date-ruler-shell">
              <div
                ref={dateRulerRef}
                className="date-ruler-track date-ruler-viewport hide-scrollbar flex overflow-x-auto pb-1 snap-x snap-mandatory scroll-smooth"
              >
                {timelineItems.map((item) => {
                  if (item.kind === "pad") {
                    return <div key={item.key} className="date-ruler-item date-ruler-item--pad shrink-0" aria-hidden="true" />;
                  }

                  const date = item.date;
                  const isSelected = date === selectedDate;
                  const isToday = date === todayIso;
                  return (
                    <button
                      key={item.key}
                      ref={(element) => {
                        if (element) {
                          dateButtonRefs.current.set(date, element);
                        } else {
                          dateButtonRefs.current.delete(date);
                        }
                      }}
                      onClick={() => {
                        userPickedDateRef.current = true;
                        setSelectedDate(date);
                      }}
                      className={`date-ruler-item snap-center shrink-0 rounded-xl border px-3 py-1.5 text-center transition-all duration-300 ${
                        isSelected
                          ? "border-blue-600 bg-blue-600 text-white shadow-[0_8px_18px_-10px_rgba(37,99,235,0.8)]"
                          : "border-slate-200 bg-slate-50 text-slate-700 hover:border-blue-300 hover:bg-blue-50"
                      }`}
                    >
                      <div className={`text-[11px] uppercase ${isSelected ? "text-blue-100" : "text-slate-500"}`}>
                        {formatWeekdayLabel(date)}
                      </div>
                      <div className="text-sm font-semibold">{formatDateLabel(date)}</div>
                      {isToday ? (
                        <div className={`text-[10px] ${isSelected ? "text-blue-100" : "text-blue-600"}`}>сегодня</div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center text-slate-500">Даты пока не найдены</div>
          )}
        </section>

        <section className="relative overflow-hidden rounded-[2rem] border border-blue-100 bg-blue-50 text-slate-800 p-4 sm:p-5 shadow-sm">
          <div className="relative mb-3 flex items-center justify-between gap-3">
            <div className="w-9" aria-hidden="true" />
            <div className="text-center text-sm sm:text-base font-semibold tracking-[0.03em] text-[#315b93]">
              Список аудиторий
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => pairsOnDate.length > 0 && setPairFilterOpen(true)}
                disabled={pairsOnDate.length === 0}
                title="Свободные аудитории"
                aria-label="Свободные аудитории"
                className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full border transition ${
                  pairFilter
                    ? "border-emerald-400 bg-emerald-500 text-white"
                    : pairsOnDate.length > 0
                    ? "border-slate-300 bg-white text-slate-600 hover:border-emerald-400 hover:text-violet-600"
                    : "border-slate-200 bg-white/50 text-slate-300 cursor-default"
                }`}
              >
                <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
                  <path d="M3 5h14M6 10h8M9 15h2" />
                </svg>
                {pairFilter && (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[9px] font-bold text-violet-600 shadow">
                    {pairFilter}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={openTeacherDialog}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 transition hover:border-blue-400 hover:text-blue-700"
                title="Найти преподавателя"
                aria-label="Найти преподавателя"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                  <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                  <line x1="16.2" y1="16.2" x2="21" y2="21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          <div className="relative flex justify-center mb-3">
            <div className="relative inline-flex items-center bg-white dark:bg-slate-700/60 border border-blue-100 dark:border-slate-600/50 rounded-full h-8 p-0.5">
              <div
                className={`absolute top-0.5 bottom-0.5 left-0.5 bg-blue-600 rounded-full transition-all duration-300 ease-in-out ${
                  selectedBuilding === "2" ? "translate-x-full" : ""
                }`}
                style={{ width: "calc(50% - 0.125rem)" }}
              />

              <button
                onClick={() => setSelectedBuilding("1")}
                className={`relative z-10 w-24 h-full text-xs font-semibold flex items-center justify-center rounded-full transition-colors duration-300 ${
                  selectedBuilding === "1" ? "text-white" : "text-blue-700 dark:text-blue-300"
                }`}
              >
                <span>1 корпус</span>
              </button>

              <button
                onClick={() => setSelectedBuilding("2")}
                className={`relative z-10 w-24 h-full text-xs font-semibold flex items-center justify-center rounded-full transition-colors duration-300 ${
                  selectedBuilding === "2" ? "text-white" : "text-blue-700 dark:text-blue-300"
                }`}
              >
                <span>2 корпус</span>
              </button>
            </div>
          </div>


          {loading ? (
            <LoadingState className="min-h-[420px]" />
          ) : error ? (
            <div className="relative text-center text-rose-600">{error}</div>
          ) : auditories.length ? (
            <div
              className="relative flex flex-wrap items-center justify-center gap-2"
              data-clip-zone={selectedBuilding === "2" ? "auditories_b2" : undefined}
            >
              {auditories.map(({ key, roomLabel }) => {
                const selected = selectedAuditory === key;
                const occupied = occupiedAuditories.has(key);
                const busyInPair = busyForPair ? busyForPair.has(key) : null;

                if (busyForPair && busyInPair) return null;

                const statusClass = showLiveOccupancy && !busyForPair
                  ? selected
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white dark:bg-slate-700/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600/50 hover:border-blue-300 dark:hover:border-blue-500"
                  : selected
                  ? "bg-blue-600 text-white border-blue-600"
                  : busyForPair
                  ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/50 hover:bg-emerald-100 dark:hover:bg-emerald-800/40"
                  : "bg-white dark:bg-slate-700/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600/50 hover:border-blue-300 dark:hover:border-blue-500";

                const isRedDot = !busyForPair && showLiveOccupancy && occupied;

                return (
                  <button
                    key={key}
                    onClick={() => setSelectedAuditory(key)}
                    className={`px-4 py-1.5 rounded-full border text-sm font-semibold transition inline-flex items-center gap-2 ${statusClass}`}
                  >
                    <span className="relative flex h-2 w-2 shrink-0">
                      {isRedDot && (
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60" />
                      )}
                      <span
                        className={`relative inline-flex h-2 w-2 rounded-full ${
                          busyForPair
                            ? "bg-emerald-500"
                            : showLiveOccupancy
                            ? occupied ? "bg-red-500" : "bg-emerald-500"
                            : "bg-blue-500"
                        } ${selected ? "ring-2 ring-white/70" : ""}`}
                      />
                    </span>
                    {roomLabel}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="relative text-center text-slate-500">
              В {selectedBuilding}-м корпусе аудитории не найдены
            </div>
          )}
        </section>

        <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1.6fr)_156px] sm:gap-4 md:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
        <section className="relative min-w-0 h-[560px]">
          {!selectedAuditory ? (
            <div className="h-full min-h-[220px] flex items-center justify-center text-slate-500 text-center bg-white/55 rounded-[28px] border border-dashed border-slate-200">
              Выберите аудиторию в верхнем блоке
            </div>
          ) : (
            <div className="relative h-full min-h-0">
              <div className="flex h-full flex-col gap-4 min-h-0">
              {referenceNow.simulated ? (
                <div className="text-center text-xs sm:text-sm rounded-xl border border-sky-200 bg-sky-50 text-sky-700 px-3 py-2">
                  Тестовый режим времени: {toLocalIsoDate(referenceNow.date)} {toHhMm(referenceNow.date)}
                </div>
              ) : null}
              <LessonCardModern
                lesson={selectedLesson}
                tone={currentPairKey && selectedPairKey === currentPairKey ? "current" : "default"}
                auditoryLabel={selectedAuditoryDisplay}
                className="flex-1 min-h-0 pr-[78px] sm:pr-0"
                teacherMap={teacherMap}
              />
              </div>
              {dayPairBlocks.length ? (
                <div
                  className="absolute right-[-7px] top-1/2 z-20 -translate-y-1/2 sm:hidden"
                  style={{ width: `${MOBILE_PICKER_TOTAL_WIDTH}px`, height: `${mobilePickerHeight}px` }}
                >
                  {/* SVG background shape */}
                  <svg
                    className="absolute inset-0 h-full w-full overflow-visible"
                    viewBox={`0 0 ${MOBILE_PICKER_TOTAL_WIDTH} ${mobilePickerHeight}`}
                    fill="none"
                    aria-hidden="true"
                  >
                    <defs>
                      <linearGradient id={`${mobilePickerIdBase}-picker-fill`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#e6f2ff" stopOpacity="0.62" />
                        <stop offset="100%" stopColor="#f2f7ff" stopOpacity="0.42" />
                      </linearGradient>
                    </defs>
                    <path
                      d={mobilePickerPath}
                      fill={`url(#${mobilePickerIdBase}-picker-fill)`}
                      stroke="rgba(255,255,255,0.55)"
                      strokeWidth="1"
                    />
                  </svg>
                  {/* Lava blob layer — animated indicator behind text */}
                  {mobilePickerSelectedIndex >= 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: MOBILE_PICKER_PADDING,
                        width: MOBILE_PICKER_BODY_WIDTH,
                        height: mobilePickerHeight - MOBILE_PICKER_PADDING * 2,
                        pointerEvents: 'none',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          left: 4,
                          width: MOBILE_PICKER_BUTTON_SIZE,
                          top: blobPos.top,
                          height: blobPos.height,
                          borderRadius: blobPos.radius,
                          transition: blobPos.transition,
                          background: 'white',
                          boxShadow: '0 2px 12px -4px rgba(37,99,235,0.24), inset 0 1px 0 rgba(255,255,255,0.95)',
                        }}
                      />
                    </div>
                  )}
                  {/* Button text layer — above blob */}
                  <div
                    className="absolute right-0 top-[4px] flex w-[55px] flex-col items-center gap-2"
                    style={{ minHeight: `${mobilePickerHeight - MOBILE_PICKER_PADDING * 2}px` }}
                  >
                    {dayPairBlocksWithGaps.map((lesson) => {
                      const pairKey = String(lesson.pairIndex || lesson.pairLabel || "");
                      const isGap = Boolean(lesson.isGap);
                      const isCurrent = currentPairKey && pairKey === currentPairKey;
                      const isSelected = selectedPairKey && pairKey === selectedPairKey;
                      const itemKey = `mobile-${selectedDate}-${lesson.pairIndex || lesson.pairLabel}`;
                      return isGap ? (
                        <div key={itemKey} className="my-1 h-px w-6 rounded-full bg-slate-300/55" />
                      ) : (
                        <button
                          key={itemKey}
                          type="button"
                          onClick={() => setSelectedPairKey(pairKey)}
                          className="relative flex h-[47px] w-[47px] items-center justify-center rounded-xl bg-transparent transition-transform duration-100 active:scale-95"
                        >
                          <span className={`text-[0.88rem] font-semibold leading-none select-none ${
                            isSelected ? "text-blue-600" : isCurrent ? "text-violet-500" : "text-slate-500"
                          }`}>
                            {lesson.pairIndex || lesson.pairLabel}
                          </span>
                          {isCurrent && !isSelected && (
                            <span className="absolute bottom-1.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-violet-400" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </section>

        <section className="hidden min-w-0 h-[560px] sm:block">
          {!selectedAuditory ? (
            <div className="h-full min-h-[160px] flex items-center justify-center rounded-[28px] border border-dashed border-slate-200 bg-white/55 text-sm text-slate-500 text-center">
              Выберите аудиторию в верхнем блоке
            </div>
          ) : (
                <aside className="flex h-full min-h-0 flex-col rounded-[24px] border border-slate-200 bg-slate-50/75 p-2 sm:rounded-2xl sm:p-4">
                  <div className="mb-2 hidden text-center text-[10px] uppercase tracking-wide text-slate-500 sm:block sm:text-xs">Пары в этой аудитории</div>
                  {dayPairBlocks.length ? (
                    <div className="relative flex-1 min-h-0">
                    <div ref={pairListRef} className="pair-list-scroll h-full min-h-0 space-y-2 overflow-y-auto px-1 pb-1 sm:px-1 sm:pr-3">
                      {dayPairBlocksWithGaps.map((lesson) => {
                        const pairKey = String(lesson.pairIndex || lesson.pairLabel || "");
                        const isGap = Boolean(lesson.isGap);
                        const isCurrent = currentPairKey && pairKey === currentPairKey;
                        const isSelected = selectedPairKey && pairKey === selectedPairKey;
                        const itemKey = `${selectedDate}-${lesson.pairIndex || lesson.pairLabel}`;
                        return (
                          isGap ? (
                            <div
                              key={itemKey}
                              ref={(el) => {
                                if (el) {
                                  pairItemRefs.current.set(pairKey, el);
                                } else {
                                  pairItemRefs.current.delete(pairKey);
                                }
                              }}
                              className="w-full rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-2.5 text-left sm:p-3"
                            >
                              <div className="flex min-h-[62px] items-center justify-center rounded-[18px] border border-white/60 bg-white/55 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] sm:hidden">
                                <div className="text-base font-semibold text-slate-500">{lesson.pairIndex || "•"}</div>
                              </div>
                              <div className="hidden sm:block">
                                <div className="text-[0.82rem] font-semibold text-slate-700 sm:text-sm">{lesson.pairLabel}</div>
                                <div className="mt-1 text-[0.7rem] text-slate-500 sm:text-xs">Окно в расписании</div>
                              </div>
                            </div>
                          ) : (
                          <button
                            key={itemKey}
                            ref={(el) => {
                              if (el) {
                                pairItemRefs.current.set(pairKey, el);
                              } else {
                                pairItemRefs.current.delete(pairKey);
                              }
                            }}
                            type="button"
                            onClick={() => setSelectedPairKey(pairKey)}
                            className={`w-full rounded-xl border bg-white p-2.5 text-left transition duration-200 ease-out sm:p-3 ${
                              isCurrent && isSelected
                                ? "translate-x-1.5 border-violet-400 ring-1 ring-violet-300 shadow-[0_10px_24px_-18px_rgba(139,92,246,0.5)]"
                                : isCurrent
                                ? "border-violet-400 ring-1 ring-violet-300"
                                : isSelected
                                ? "translate-x-1.5 border-blue-400 ring-1 ring-blue-300 shadow-[0_10px_24px_-18px_rgba(37,99,235,0.55)]"
                                : "border-slate-200 hover:border-blue-300"
                            }`}
                          >
                            <div
                              className={`relative flex min-h-[62px] flex-col items-center justify-center overflow-hidden rounded-[20px] border text-center sm:hidden ${
                                isSelected
                                  ? "border-white/75 bg-[radial-gradient(circle_at_35%_28%,rgba(255,255,255,0.96),rgba(255,255,255,0.64)_38%,rgba(219,234,254,0.56)_66%,rgba(191,219,254,0.42)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.98),0_18px_34px_-24px_rgba(37,99,235,0.55)] backdrop-blur-[16px]"
                                  : isCurrent
                                  ? "border-violet-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(245,243,255,0.72))] shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_14px_28px_-22px_rgba(139,92,246,0.34)]"
                                  : "border-white/60 bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(248,250,252,0.72))] shadow-[inset_0_1px_0_rgba(255,255,255,0.92)]"
                              }`}
                            >
                              {isSelected ? (
                                <>
                                  <div className="pointer-events-none absolute inset-x-2 top-0 h-5 rounded-b-full bg-white/70 blur-md" />
                                  <div className="pointer-events-none absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/55 bg-white/10 blur-[1px]" />
                                </>
                              ) : null}
                              <div className={`relative text-[1.05rem] font-semibold leading-none ${isCurrent ? "text-violet-600" : isSelected ? "scale-[1.18] text-blue-700" : "text-slate-700"}`}>
                                {lesson.pairIndex || lesson.pairLabel}
                              </div>
                              {isCurrent || isSelected ? (
                                <div className={`relative mt-1 text-[0.58rem] font-medium ${isSelected ? "text-blue-500" : "text-slate-400"}`}>
                                  {lesson.timeRange}
                                </div>
                              ) : null}
                            </div>
                            <div className="hidden sm:block">
                              <div className="text-[0.82rem] font-semibold text-slate-800 sm:text-sm">{lesson.pairLabel}</div>
                              <div className="mb-2 text-[0.7rem] text-slate-500 sm:text-xs">{lesson.timeRange}</div>
                              <div className="text-[0.82rem] leading-snug text-slate-700 sm:text-sm">{lesson.discipline}</div>
                              <div className="mt-1 text-[0.7rem] text-slate-500 sm:text-xs">{lesson.groups}</div>
                            </div>
                          </button>
                          )
                        );
                      })}
                    </div>
                    {pairListFade.top ? (
                      <div className="pointer-events-none absolute inset-x-0 top-0 h-10 rounded-t-xl bg-gradient-to-b from-slate-200/95 via-slate-100/82 to-transparent" />
                    ) : null}
                    {pairListFade.bottom ? (
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 rounded-b-xl bg-gradient-to-t from-slate-200/95 via-slate-100/84 to-transparent" />
                    ) : null}
                    </div>
                  ) : (
                    <div className="min-h-[160px] flex items-center justify-center text-sm text-slate-500 text-center">
                      На выбранную дату пар нет
                    </div>
                  )}
                </aside>
          )}
        </section>
        </div>
      </div>

      <dialog
        ref={teacherDialogRef}
        className="teacher-search-dialog w-[min(92vw,36rem)] rounded-2xl p-0"
      >
        <div data-dialog-panel className="teacher-search-panel bg-white dark:bg-slate-900 rounded-2xl p-5 sm:p-6">
          <div data-clip-zone="teacher_search" style={{ height: 0 }} aria-hidden="true" />
          <div className="mb-5 flex items-center justify-between gap-3">
            <h3 className="text-[17px] font-semibold tracking-tight text-slate-900 dark:text-slate-100">Поиск преподавателя</h3>
            <button
              type="button"
              onClick={closeTeacherDialog}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 transition hover:bg-slate-200 dark:hover:bg-slate-700"
              aria-label="Закрыть"
            >
              <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 4l12 12M16 4L4 16" />
              </svg>
            </button>
          </div>

          <div className="mb-4 rounded-2xl border border-sky-100 dark:border-sky-900/40 bg-sky-50 dark:bg-sky-950/30 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-sky-100 dark:border-sky-900/40 bg-white dark:bg-slate-800">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="text-sky-600 dark:text-sky-400">
                  <path d="M3 20h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M5 20V9l7-4 7 4v11" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
                  <path d="M9 20v-5h6v5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
                  <path d="M11 10h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>
              <div>
                <div className="text-[13px] font-semibold text-slate-800 dark:text-slate-200">Быстрый локатор</div>
                <div className="text-[12px] text-slate-500 dark:text-slate-400">Текущая и следующая аудитория по расписанию</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-800/60 px-3 py-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">Время</div>
                <div className="mt-0.5 text-[13px] font-semibold text-slate-800 dark:text-slate-200">{teacherSearchNowLabel}</div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-800/60 px-3 py-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">Корпус</div>
                <div className="mt-0.5 text-[13px] font-semibold text-slate-800 dark:text-slate-200">{selectedBuilding === "2" ? "2 корпус" : "1 корпус"}</div>
              </div>
            </div>
          </div>

          <form onSubmit={handleTeacherSearch} className="space-y-2.5">
            <div className="relative">
              <input
                value={teacherQuery}
                onChange={(e) => {
                  setTeacherQuery(e.target.value);
                  setShowTeacherSuggestions(true);
                }}
                onFocus={() => {
                  if (teacherBlurTimerRef.current) {
                    clearTimeout(teacherBlurTimerRef.current);
                    teacherBlurTimerRef.current = null;
                  }
                  setShowTeacherSuggestions(true);
                }}
                onBlur={() => {
                  teacherBlurTimerRef.current = setTimeout(() => {
                    setShowTeacherSuggestions(false);
                  }, 150);
                }}
                placeholder="Фамилия преподавателя"
                title="Введите фамилию или её часть"
                className="h-12 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 text-[14px] text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/10"
              />
              {showTeacherSuggestions && filteredTeacherSuggestions.length > 0 ? (
                <div className="teacher-suggestions-list absolute z-20 mt-1.5 w-full max-h-56 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-950/40">
                  {filteredTeacherSuggestions.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setTeacherQuery(name);
                        setShowTeacherSuggestions(false);
                      }}
                      className="block w-full px-4 py-2.5 text-left text-[13px] text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition"
                    >
                      {name}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button
              type="submit"
              disabled={teacherSearchLoading}
              className="h-11 w-full rounded-xl bg-blue-600 text-[14px] font-semibold text-white transition hover:bg-blue-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {teacherSearchLoading ? "Ищу..." : "Найти"}
            </button>
          </form>

          {teacherSearchError ? (
            <div className="mt-3 rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 px-3 py-2.5 text-[13px] text-rose-700 dark:text-rose-400">
              {teacherSearchError}
            </div>
          ) : null}

          {teacherSearchResult ? (
            <div className="mt-4 space-y-2">
              <div className="text-[11px] text-slate-400 dark:text-slate-500">
                Результат по запросу: <span className="font-semibold text-slate-600 dark:text-slate-400">{teacherSearchResult.query}</span>
              </div>

              <div className="rounded-xl border border-slate-100 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-800/40 p-4">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">Сейчас</div>
                {teacherSearchResult.current ? (
                  <div>
                    <div className="text-[22px] font-bold tracking-tight text-slate-900 dark:text-slate-100">
                      {teacherSearchResult.current.auditory || "Ауд. не указана"}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-slate-500 dark:text-slate-400">
                      {teacherSearchResult.current.building && <span>{teacherSearchResult.current.building} корпус</span>}
                      {teacherSearchResult.current.time_start && (
                        <span className="font-mono">{teacherSearchResult.current.time_start}{teacherSearchResult.current.time_end ? `–${teacherSearchResult.current.time_end}` : ""}</span>
                      )}
                      {teacherSearchResult.current.group && <span>{teacherSearchResult.current.group}</span>}
                    </div>
                  </div>
                ) : (
                  <div className="text-[13px] text-slate-400 dark:text-slate-500">Пара сейчас не идёт</div>
                )}
              </div>

              <div className="rounded-xl border border-slate-100 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-800/40 p-4">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">Следующая пара</div>
                {teacherSearchResult.next ? (
                  <div>
                    <div className="text-[22px] font-bold tracking-tight text-slate-900 dark:text-slate-100">
                      {teacherSearchResult.next.auditory || "Ауд. не указана"}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-slate-500 dark:text-slate-400">
                      {teacherSearchResult.next.building && <span>{teacherSearchResult.next.building} корпус</span>}
                      {teacherSearchResult.next.date && <span>{teacherSearchResult.next.date}</span>}
                      {teacherSearchResult.next.time_start && (
                        <span className="font-mono">{teacherSearchResult.next.time_start}{teacherSearchResult.next.time_end ? `–${teacherSearchResult.next.time_end}` : ""}</span>
                      )}
                      {teacherSearchResult.next.group && <span>{teacherSearchResult.next.group}</span>}
                    </div>
                  </div>
                ) : (
                  <div className="text-[13px] text-slate-400 dark:text-slate-500">Следующих пар не найдено</div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </dialog>
      <style>{`
        html {
          scrollbar-gutter: stable;
        }
        .teacher-search-dialog {
          border: 0;
          padding: 0;
          background: transparent;
          overflow: visible;
          margin: auto;
          box-shadow: 0 20px 60px -20px rgba(15, 23, 42, 0.55);
        }
        .teacher-search-dialog[open] {
          animation: teacherDialogFromBottom 260ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .teacher-search-dialog::backdrop {
          background: rgba(15, 23, 42, 0.38);
          opacity: 0;
        }
        .teacher-search-dialog[open]::backdrop {
          animation: teacherDialogBackdropIn 180ms ease-out forwards;
        }
        .teacher-search-panel {
          border-radius: 1rem;
          overflow: visible;
        }
        .teacher-suggestions-list {
          scrollbar-width: none;
        }
        .pair-list-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .pair-list-scroll::-webkit-scrollbar {
          width: 0;
          height: 0;
        }
        .teacher-suggestions-list::-webkit-scrollbar {
          width: 0;
          height: 0;
        }
        @keyframes teacherDialogBackdropIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes teacherDialogFromBottom {
          from {
            opacity: 0;
            transform: translateY(44px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .teacher-search-dialog[open]::backdrop,
          .teacher-search-dialog[open] {
            animation: none !important;
          }
        }
      `}</style>

      {pairFilterOpen && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-end justify-center sm:items-center"
          onClick={() => setPairFilterOpen(false)}
        >
          <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-[3px]" />
          <div
            className="pair-filter-sheet relative w-full max-w-sm overflow-hidden rounded-t-[2rem] sm:rounded-[2rem] bg-white dark:bg-slate-900 shadow-[0_-24px_60px_-20px_rgba(15,23,42,0.45)] sm:shadow-[0_24px_60px_-20px_rgba(15,23,42,0.45)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div>
                <div className="text-[17px] font-bold tracking-[-0.02em] text-slate-900 dark:text-slate-100">Свободные аудитории</div>
                <div className="mt-0.5 text-[12px] text-slate-400 dark:text-slate-500">Выберите пару для фильтрации</div>
              </div>
              <button
                type="button"
                onClick={() => setPairFilterOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 transition hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 4l12 12M16 4L4 16" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto px-3 pb-5" style={{ maxHeight: "60vh" }} data-clip-zone="free_auditories">
              {pairFilter && (
                <button
                  type="button"
                  onClick={() => { setPairFilter(null); setPairFilterOpen(false); }}
                  className="mb-2 flex w-full items-center gap-2.5 rounded-2xl border border-slate-200 dark:border-slate-700 px-4 py-3 text-left text-[13px] font-semibold text-slate-500 dark:text-slate-400 transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                >
                  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <path d="M4 4l12 12M16 4L4 16" />
                  </svg>
                  Сбросить фильтр
                </button>
              )}
              {pairsOnDate.map((p) => {
                const active = pairFilter === p.index;
                const freeCount = freeCountByPair.get(p.index) ?? 0;
                const hasFree = freeCount > 0;
                return (
                  <button
                    key={p.index}
                    type="button"
                    onClick={() => { setPairFilter(active ? null : p.index); setPairFilterOpen(false); }}
                    className={`mb-2 flex w-full items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition ${
                      active
                        ? "border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/30"
                        : "border-slate-200 bg-white hover:border-emerald-200 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800/40 dark:hover:border-emerald-700 dark:hover:bg-slate-800"
                    }`}
                  >
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[15px] font-bold ${
                      active ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                    }`}>
                      {p.index}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-[14px] font-semibold ${active ? "text-emerald-700 dark:text-emerald-400" : "text-slate-800 dark:text-slate-200"}`}>
                        {p.label || `${p.index} пара`}
                      </div>
                      {p.timeStart && (
                        <div className="mt-0.5 text-[12px] text-slate-400 dark:text-slate-500">
                          {p.timeStart}{p.timeEnd ? ` — ${p.timeEnd}` : ""}
                        </div>
                      )}
                    </div>
                    <div className="ml-auto flex shrink-0 items-center gap-1.5">
                      {hasFree ? (
                        <>
                          <span className="pair-free-dot" />
                          <span className="text-[12px] font-semibold text-violet-600 dark:text-violet-400">{freeCount}</span>
                        </>
                      ) : (
                        <span className="text-[11px] text-slate-300 dark:text-slate-600">занято</span>
                      )}
                      {active && (
                        <svg className="text-violet-500" viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                          <path d="M4 10l4 4 8-8" />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        .pair-filter-sheet {
          animation: pairFilterSheetIn 300ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes pairFilterSheetIn {
          from { opacity: 0; transform: translateY(32px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (min-width: 640px) {
          .pair-filter-sheet {
            animation: pairFilterSheetInDesktop 260ms cubic-bezier(0.16, 1, 0.3, 1) both;
          }
          @keyframes pairFilterSheetInDesktop {
            from { opacity: 0; transform: scale(0.95) translateY(8px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
        }
        .pair-free-dot {
          display: inline-block;
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #10b981;
          animation: pairFreePulse 1.6s ease-in-out infinite;
        }
        @keyframes pairFreePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.45; transform: scale(0.7); }
        }
      `}</style>
    </>
  );
}
